<template>
  <section class="table-wrap">
    <ve-table      
      :columns="columns"
      :table-data="tableData"
      :border-around="false"
      :sort-option="sortOption"      
    />
    

    <empty-state
      v-if="!isLoading && !subaccounts.length"
      :title="emptyMessage"
    />

    <div v-if="isLoading" class="subaccount--loader">
      <spinner />
      <span>{{ $t('SUBACCOUNTS.LIST.LOADING_MESSAGE') }}</span>
    </div>
  </section>
</template>

<script>
import { VeTable } from 'vue-easytable';

import Spinner from 'shared/components/Spinner';
import EmptyState from 'dashboard/components/widgets/EmptyState';

import { mixin as clickaway } from 'vue-clickaway';

export default {
  components: {
    EmptyState,
    Spinner,
    VeTable,
  },
  mixins: [
    clickaway,
  ],
  props: {
    subaccounts: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    emptyMessage: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      sortConfig: {},
      sortOption: {
        sortAlways: true,
        sortChange: params => this.$emit('on-sort-change', params),
      },
    };
  },
  computed: {
    tableData() {
      if (this.isLoading) {
        return [];
      }
      return this.subaccounts;
    },
    columns() {
      return [
        {
          field: 'name',
          key: 'name',
          title: this.$t('SUBACCOUNTS.LIST.TABLE_HEADER.NAME'),          
          align: this.isRTLView ? 'right' : 'left',
          sortBy: this.sortConfig.name || '',
          renderBodyCell: ({ row }) => (
            row.status === 'active' ?
            <a 
              href={`/app/accounts/${row.id}/dashboard`}              
            >
              {row.name}
            </a>
            : <span>{row.name}</span>
          ),
        },
        {
          field: 'status',
          key: 'status',
          title: this.$t('SUBACCOUNTS.LIST.TABLE_HEADER.STATUS'),          
          align: this.isRTLView ? 'right' : 'left',
          sortBy: this.sortConfig.status || '',
          renderBodyCell: ({ row }) => (
            <span>
              {row.status === 'active'
                ? this.$t('SUBACCOUNTS.LIST.STATUS.ACTIVE')
                : this.$t('SUBACCOUNTS.LIST.STATUS.INACTIVE')}
            </span>
          ),
        },
        {
          field: 'user_count',
          key: 'user_count',
          title: this.$t('SUBACCOUNTS.LIST.TABLE_HEADER.USER_COUNT'),
          align: 'center',          
          renderBodyCell: ({ row }) => (
            row.status === 'active' ?
              <a 
                href={`/app/accounts/${row.id}/settings/agents/list`}              
              >
                {row.users_count}
              </a>
              : <span>{row.users_count}</span>
          ),
        },
        {
          field: 'inbox_count',
          key: 'inbox_count',
          title: this.$t('SUBACCOUNTS.LIST.TABLE_HEADER.INBOX_COUNT'),          
          align: 'center',          
          renderBodyCell: ({ row }) => (
            row.status === 'active' ?
            <a 
              href={`/app/accounts/${row.id}/settings/inboxes/list`}              
            >
              {row.inboxes_count}
            </a> 
            : <span>{row.inboxes_count}</span>
          ),
        },
        {
          field: 'enable_template_limit',
          key: 'enable_template_limit',
          title: this.$t('SUBACCOUNTS.LIST.TABLE_HEADER.TEMPLATE_LIMIT'),
          align: 'center',
          renderBodyCell: ({ row }) => (
            <span>{this.booleanLabel(row.enable_template_limit)}</span>
          ),
        },
        {
          field: 'password_policy_expire',
          key: 'password_policy_expire',
          title: this.$t('SUBACCOUNTS.LIST.TABLE_HEADER.PASSWORD_POLICY'),
          align: 'center',
          renderBodyCell: ({ row }) => (
            <span>
              {this.booleanLabel(
                row.custom_attributes?.password_policy_expire?.enable
              )}
            </span>
          ),
        },
        {
          field: 'access_time_control',
          key: 'access_time_control',
          title: this.$t('SUBACCOUNTS.LIST.TABLE_HEADER.ACCESS_TIME_POLICY'),
          align: 'center',
          renderBodyCell: ({ row }) => (
            <span>
              {this.booleanLabel(
                row.custom_attributes?.access_time_control?.enable
              )}
            </span>
          ),
        },
      ];
    }
  },
  methods: {
    booleanLabel(value) {
      return value
        ? this.$t('SUBACCOUNTS.LIST.BOOLEAN.YES')
        : this.$t('SUBACCOUNTS.LIST.BOOLEAN.NO');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~dashboard/assets/scss/mixins';

.table-wrap {
  flex: 1 1;
  height: 100%;
  overflow: hidden;
}

.table-wrap::v-deep {
  .ve-table {
    padding-bottom: var(--space-large);
  }

  .row--subaccount-block {
    align-items: center;
    display: flex;
    text-align: left;

    .subaccount-block {
      align-items: flex-start;
      display: flex;
      flex-direction: column;
      margin: 0 var(--space-small);
      max-width: var(--space-giga);

      h6 {
        color: var(--c-500);
        text-transform: capitalize;
        font-size: var(--font-size-small);
      }
    }

    .view-details--button {
      color: var(--b-800);
    }
  }

  .ve-table-header-th {
    padding: var(--space-small) var(--space-two) !important;
    font-size: var(--font-size-mini) !important;
  }

  .ve-table-body-td {
    padding: var(--space-small) var(--space-two) !important;

    a {
      font-weight: var(--font-weight-medium);
    }
  }

  .ve-table-sort {
    top: -4px;
  }
}

.subaccount--loader {
  align-items: center;
  display: flex;
  font-size: var(--font-size-default);
  justify-content: center;
  padding: var(--space-big);
}

.button-wrapper {
  justify-content: space-evenly;
  display: flex;
  flex-direction: row;
  min-width: 20rem;
}
</style>
